import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Empty,
  Input,
  Popconfirm,
  Row,
  Col,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../api/api";

const { Title, Text } = Typography;
const { Search } = Input;

const STATUS_OPTIONS = [
  { label: "Draft", value: "draft" },
  { label: "Published", value: "published" },
  { label: "Archived", value: "archived" },
];

const TOUR_OPERATOR_OPTIONS = [
  { label: "Caribe Sol", value: "CAH" },
  { label: "Enjoy Travel Group", value: "ETG" },
  { label: "Holasun", value: "HOL" },
  { label: "Sunquest Vacations", value: "SQV" },
  { label: "The Special Travel", value: "STC" },
  { label: "Sunwing Vacations", value: "SWG" },
  { label: "TravelBrands", value: "TBA" },
  { label: "Air Canada Vacations", value: "VAC" },
  { label: "Transat", value: "VAT" },
  { label: "Vacances Westjet Quebec", value: "VWQ" },
  { label: "WestJet Vacations", value: "WJS" },
];

const TOUR_OPERATOR_MAP = TOUR_OPERATOR_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const DealManagementPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const [deals, setDeals] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [operatorFilter, setOperatorFilter] = useState(undefined);

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}deals`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await res.json();

      if (res.ok && result.success) {
        setDeals(Array.isArray(result.data) ? result.data : []);
      } else {
        message.error(result.error || "Failed to load deals");
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to load deals");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  const handleDelete = async (record) => {
    try {
      const res = await fetch(`${API_BASE_URL}deals`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id: record.id }),
      });
      const result = await res.json();

      if (res.ok && result.success) {
        message.success("Deal deleted");
        fetchDeals();
      } else {
        message.error(result.error || "Failed to delete deal");
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to delete deal");
    }
  };

  const filteredDeals = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();

    return deals.filter((deal) => {
      const matchesSearch =
        !keyword ||
        String(deal.id).includes(keyword) ||
        (deal.tour_operator_code || "").toLowerCase().includes(keyword) ||
        (TOUR_OPERATOR_MAP[deal.tour_operator_code] || "")
          .toLowerCase()
          .includes(keyword) ||
        (deal.deal_header_en || "").toLowerCase().includes(keyword) ||
        (deal.deal_header_zh_cn || "").toLowerCase().includes(keyword) ||
        (deal.deal_header_zh_tw || "").toLowerCase().includes(keyword);

      const matchesStatus = !statusFilter || deal.status === statusFilter;
      const matchesOperator =
        !operatorFilter || deal.tour_operator_code === operatorFilter;

      return matchesSearch && matchesStatus && matchesOperator;
    });
  }, [deals, operatorFilter, searchText, statusFilter]);

  const columns = [
    {
      title: "Deal",
      key: "deal_header_en",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.deal_header_en || "-"}</Text>
          <Text type="secondary">#{record.id}</Text>
        </Space>
      ),
    },
    {
      title: "Tour Operator",
      dataIndex: "tour_operator_code",
      key: "tour_operator_code",
      width: 180,
      render: (value) => TOUR_OPERATOR_MAP[value] || value || "-",
    },
    {
      title: "Booking Window",
      key: "booking_window",
      width: 220,
      render: (_, record) =>
        `${record.booking_start_date || "-"} to ${record.booking_end_date || "-"}`,
    },
    {
      title: "Departure Window",
      key: "departure_window",
      width: 220,
      render: (_, record) =>
        `${record.departure_start_date || "-"} to ${record.departure_end_date || "-"}`,
    },
    {
      title: "Deal Link",
      dataIndex: "deal_url",
      key: "deal_url",
      width: 160,
      render: (value) =>
        value ? (
          <Button
            type="link"
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            icon={<LinkOutlined />}
            style={{ padding: 0 }}
          >
            Open Link
          </Button>
        ) : (
          "-"
        ),
    },
    {
      title: "Destinations",
      key: "destination_count",
      width: 120,
      render: (_, record) => record.destination_codes?.length || 0,
    },
    {
      title: "Departures",
      key: "departure_count",
      width: 120,
      render: (_, record) => record.departure_iata_codes?.length || 0,
    },
    {
      title: "Durations",
      key: "duration_days",
      width: 140,
      render: (_, record) =>
        (record.duration_days || []).length > 0
          ? record.duration_days.join(", ")
          : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (value) => (
        <Tag color={value === "published" ? "green" : value === "archived" ? "default" : "gold"}>
          {value}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 170,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => navigate(`/deals/${record.id}`)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete deal?"
            description="This will remove the deal and all linked destinations, departures, and durations."
            okText="Delete"
            cancelText="Cancel"
            onConfirm={() => handleDelete(record)}
          >
            <Button danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ marginBottom: 4 }}>
            Tour Operator Deals
          </Title>
          <Text type="secondary">
            Manage promotional deals and their linked destinations, departures, and durations.
          </Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/deals/new")}
          >
            New Deal
          </Button>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={12}>
          <Col xs={24} md={10}>
            <Search
              allowClear
              placeholder="Search by id, operator, or deal header"
              onSearch={setSearchText}
              onChange={(event) => setSearchText(event.target.value)}
              value={searchText}
            />
          </Col>
          <Col xs={24} md={7}>
            <Select
              allowClear
              placeholder="Filter by operator"
              options={TOUR_OPERATOR_OPTIONS}
              style={{ width: "100%" }}
              value={operatorFilter}
              onChange={setOperatorFilter}
            />
          </Col>
          <Col xs={24} md={7}>
            <Select
              allowClear
              placeholder="Filter by status"
              options={STATUS_OPTIONS}
              style={{ width: "100%" }}
              value={statusFilter}
              onChange={setStatusFilter}
            />
          </Col>
        </Row>
      </Card>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={filteredDeals}
          columns={columns}
          pagination={false}
          locale={{ emptyText: <Empty description="No deals found" /> }}
        />
      </Card>
    </div>
  );
};

export default DealManagementPage;
